// TodaysMission.jsx - Daily call list for staff (the "spaceship" UI)
// Shows priorities: STALLED first, then Hot, then Day 1-2, then cadence calls

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { ConfirmQueuePanel } from "./ConfirmQueue";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
  hot:"#dc2626", warm:"#f59e0b",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

// ★ Same lead kinds as the pipeline board (owner 2026-10-16).
const NEW_LEAD_HOURS = 72;
const isColdList = (l) => l.source === "cold_outreach";
const isNewLead = (l) => !isColdList(l) && l.created_at && (Date.now() - new Date(l.created_at).getTime()) < NEW_LEAD_HOURS * 3600000;

export default function TodaysMission() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filter, setFilter] = useState("all"); // 'all' | 'stalled' | 'hot' | 'callbacks' | 'today'
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("mission-board")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, () => load())
      .subscribe();
    
    // Auto-refresh every 30 sec
    const interval = setInterval(() => setRefreshKey(k => k + 1), 30000);
    
    return () => { 
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [filter]);

  useEffect(() => { load(); }, [refreshKey]);

  const load = async () => {
    // Load smart call queue. ★ New leads are fetched on their own as well
    //   (owner 2026-10-16): ranked by priority_score alone, not one of the 25
    //   new Facebook leads from the last 3 days made the top 300 — the list
    //   was all old cadence leads and the desk never saw the fresh ones.
    const since = new Date(Date.now() - 72 * 3600000).toISOString();
    const [{ data: q }, { data: fresh }] = await Promise.all([
      supabase.from("smart_call_queue").select("*").order("priority_score", { ascending: false }).limit(300),
      supabase.from("smart_call_queue").select("*").gte("created_at", since).neq("source", "cold_outreach").order("created_at", { ascending: false }).limit(200),
    ]);
    const seen = new Set();
    const merged = [...(fresh || []), ...(q || [])].filter(l => !seen.has(l.id) && seen.add(l.id));

    setQueue(merged);

    // Load stats
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const [{ count: bookedToday }, { count: callsToday }, { count: stalledNow }, { count: hotNow }] = await Promise.all([
      supabase.from("leads").select("*", { count: "exact", head: true }).eq("pipeline_status", "booked").gte("pipeline_status_changed_at", todayStart.toISOString()),
      supabase.from("lead_activities").select("*", { count: "exact", head: true }).eq("activity_type", "call_started").gte("created_at", todayStart.toISOString()),
      supabase.from("leads").select("*", { count: "exact", head: true }).eq("bot_conversation_state", "stalled"),
      supabase.from("leads").select("*", { count: "exact", head: true }).eq("lead_temperature", "hot").not("pipeline_status", "in", '("booked","lost")'),
    ]);

    setStats({
      bookedToday: bookedToday || 0,
      callsToday: callsToday || 0,
      stalledNow: stalledNow || 0,
      hotNow: hotNow || 0,
    });
  };

  const filtered = queue.filter(l => {
    if (filter === "stalled") return l.bot_conversation_state === "stalled";
    if (filter === "hot") return l.lead_temperature === "hot";
    if (filter === "callbacks") return l.pipeline_status === "callback_scheduled";
    if (filter === "today") {
      const today = new Date(); today.setHours(0,0,0,0);
      return !isColdList(l) && new Date(l.created_at) >= today;
    }
    return true;
  });

  // Group by priority "tier". ★ New leads come right after stalled chats
  //   (owner 2026-10-16): brand-new never-called first, then new leads who
  //   didn't answer; the cold-outreach list gets its own tier at the bottom.
  const stalled = filtered.filter(l => l.bot_conversation_state === "stalled");
  const newFirst = filtered.filter(l => !stalled.includes(l) && isNewLead(l) && (l.call_attempts || 0) === 0);
  const newBack = filtered.filter(l => !stalled.includes(l) && isNewLead(l) && (l.call_attempts || 0) > 0);
  const coldList = filtered.filter(l => !stalled.includes(l) && isColdList(l));
  const taken = (l) => stalled.includes(l) || newFirst.includes(l) || newBack.includes(l) || coldList.includes(l);
  const callbacks = filtered.filter(l => !taken(l) && l.pipeline_status === "callback_scheduled" && new Date(l.callback_scheduled_at) <= new Date(Date.now() + 30*60000));
  const hot = filtered.filter(l => !taken(l) && l.lead_temperature === "hot" && l.pipeline_status !== "callback_scheduled");
  const day12 = filtered.filter(l => !taken(l) && l.cadence_day <= 2 && l.lead_temperature !== "hot" && l.pipeline_status !== "callback_scheduled");
  const others = filtered.filter(l => !taken(l) && !callbacks.includes(l) && !hot.includes(l) && !day12.includes(l));

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <div style={{fontSize:24,fontWeight:800,color:C.text,fontFamily:S}}>🎯 Today's Mission</div>
          <span style={{padding:"3px 10px",background:C.greenL,color:C.green,borderRadius:50,fontSize:10,fontWeight:700}}>● LIVE</span>
        </div>
        <div style={{fontSize:12,color:C.muted}}>Your prioritized call list - work top to bottom for max conversion</div>
      </div>

      {/* Confirmation calls come FIRST — these are already-booked revenue
          that evaporates without a call, which beats any lead on this page. */}
      <ConfirmQueuePanel userName={undefined}/>

      {/* Stats bar */}
      {stats && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginBottom:18}}>
          <BigStat label="🔥 STALLED" value={stats.stalledNow} color={C.hot} highlight={stats.stalledNow > 0}/>
          <BigStat label="🌡 Hot Now" value={stats.hotNow} color={C.warm}/>
          <BigStat label="📞 Calls Today" value={stats.callsToday} color={C.blue}/>
          <BigStat label="✅ Booked Today" value={stats.bookedToday} color={C.green}/>
        </div>
      )}

      {/* STALLED ALERT */}
      {stalled.length > 0 && (
        <div style={{padding:14,background:"linear-gradient(135deg,rgba(220,38,38,0.15),rgba(220,38,38,0.05))",border:"2px solid "+C.hot,borderRadius:12,marginBottom:16,animation:"pulse 2s infinite"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{fontSize:24}}>🔥</span>
            <div style={{fontSize:16,fontWeight:800,color:C.hot,fontFamily:S}}>URGENT: {stalled.length} customer{stalled.length > 1 ? "s" : ""} engaged but went silent!</div>
          </div>
          <div style={{fontSize:12,color:C.text}}>These customers were just chatting with the bot. They're HOT - call them within 5 minutes for best chance!</div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {[
          ["all", "All", queue.length],
          ["stalled", "🔥 Stalled", stats?.stalledNow || 0],
          ["hot", "🌡 Hot", stats?.hotNow || 0],
          ["callbacks", "⏰ Callbacks", queue.filter(l => l.pipeline_status === "callback_scheduled").length],
          ["today", "🆕 Today", queue.filter(l => { const t = new Date(); t.setHours(0,0,0,0); return !isColdList(l) && new Date(l.created_at) >= t; }).length],
        ].map(([id, label, count]) => (
          <button key={id} onClick={() => setFilter(id)} style={{
            padding:"7px 14px",
            background:filter === id ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : C.light,
            color:filter === id ? "#fff" : C.muted,
            border: "none",
            borderRadius:50,
            fontSize:11,
            fontWeight:filter===id ? 700 : 600,
            cursor:"pointer",
            fontFamily:F,
            display:"flex",
            alignItems:"center",
            gap:5,
          }}>
            <span>{label}</span>
            <span style={{padding:"1px 7px",background:filter===id?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.06)",borderRadius:50,fontSize:10,fontWeight:700}}>{count}</span>
          </button>
        ))}
      </div>

      {/* Lists by tier */}
      {stalled.length > 0 && filter !== "callbacks" && filter !== "hot" && (
        <Tier title="🔥 STALLED - Call NOW" subtitle="Customer was chatting and went quiet" color={C.hot} leads={stalled} onSelect={setSelectedLead}/>
      )}
      {newFirst.length > 0 && filter !== "stalled" && filter !== "callbacks" && (
        <Tier title="🆕 NEW LEADS — Call First" subtitle="Came in the last 3 days, not called yet" color={C.green} leads={newFirst} onSelect={setSelectedLead}/>
      )}
      {newBack.length > 0 && filter !== "stalled" && (
        <Tier title="🔁 New Leads — Call Back" subtitle="New leads that didn't answer yet — try again" color={C.warm} leads={newBack} onSelect={setSelectedLead}/>
      )}
      {callbacks.length > 0 && filter !== "stalled" && filter !== "hot" && (
        <Tier title="⏰ Callbacks Due" subtitle="Scheduled calls within 30 minutes" color={C.purple} leads={callbacks} onSelect={setSelectedLead}/>
      )}
      {hot.length > 0 && filter !== "callbacks" && filter !== "stalled" && (
        <Tier title="🌡 Hot Leads" subtitle="High-value, engaged" color={C.warm} leads={hot} onSelect={setSelectedLead}/>
      )}
      {day12.length > 0 && filter === "all" && (
        <Tier title="🚀 Day 1-2 Leads" subtitle="Fresh - max conversion potential" color={C.gold} leads={day12} onSelect={setSelectedLead}/>
      )}
      {others.length > 0 && (filter === "all" || filter === "today") && (
        <Tier title="📞 Other Cadence Calls" subtitle="Active follow-ups" color={C.blue} leads={others} onSelect={setSelectedLead}/>
      )}
      {coldList.length > 0 && filter === "all" && (
        <Tier title="❄️ Cold List" subtitle="Old contacts from the outreach list — only after the new leads" color={C.muted} leads={coldList} onSelect={setSelectedLead}/>
      )}

      {filtered.length === 0 && (
        <div style={{padding:50,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:12}}>
          <div style={{fontSize:48,marginBottom:10}}>🎉</div>
          <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:6,fontFamily:S}}>All caught up!</div>
          <div style={{fontSize:12,color:C.muted}}>No urgent calls right now. Great job!</div>
        </div>
      )}

      {selectedLead && <LeadCallPanel lead={selectedLead} onClose={() => { setSelectedLead(null); load(); }}/>}
    </div>
  );
}

// ──────────────────────────────────────────────
function Tier({ title, subtitle, color, leads, onSelect }) {
  if (leads.length === 0) return null;
  return (
    <div style={{marginBottom:20}}>
      <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:8,paddingBottom:6,borderBottom:"2px solid "+color+"30"}}>
        <div style={{fontSize:14,fontWeight:800,color:color,fontFamily:S}}>{title}</div>
        <div style={{fontSize:11,color:C.muted,flex:1}}>{subtitle}</div>
        <div style={{fontSize:11,color:color,fontWeight:700}}>{leads.length}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {leads.map(l => <CallRow key={l.id} lead={l} onClick={() => onSelect(l)}/>)}
      </div>
    </div>
  );
}

function CallRow({ lead, onClick }) {
  const minsAgo = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 60000);
  const ago = minsAgo < 60 ? `${minsAgo}m` : minsAgo < 1440 ? `${Math.floor(minsAgo/60)}h` : `${Math.floor(minsAgo/1440)}d`;
  const isStalled = lead.bot_conversation_state === "stalled";
  const isCallback = lead.pipeline_status === "callback_scheduled";
  const callbackTime = lead.callback_scheduled_at ? new Date(lead.callback_scheduled_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : null;

  return (
    <div onClick={onClick} style={{
      padding: "12px 16px",
      background: isStalled ? "linear-gradient(135deg,rgba(220,38,38,0.08),rgba(220,38,38,0.02))" : C.surface,
      border: "1px solid " + (isStalled ? C.hot+"60" : C.border),
      borderRadius: 9,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 14,
      transition: "all 0.15s",
    }}
    onMouseEnter={e=>e.currentTarget.style.transform="translateX(4px)"}
    onMouseLeave={e=>e.currentTarget.style.transform="translateX(0)"}>
      
      {/* Avatar / icon */}
      <div style={{
        width:44, height:44, borderRadius:"50%",
        background: isStalled ? C.hot : lead.lead_temperature === "hot" ? C.warm : C.gold,
        color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, fontWeight: 700, flexShrink: 0,
      }}>
        {(lead.full_name || "?").charAt(0).toUpperCase()}
      </div>

      {/* Info */}
      <div style={{flex:1, minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
          <span style={{fontSize:14,fontWeight:700,color:C.text}}>{lead.full_name || "No name"}</span>
          {isStalled && <span style={{fontSize:9,padding:"1px 6px",background:C.hot,color:"#fff",borderRadius:50,fontWeight:700}}>STALLED</span>}
          {isCallback && <span style={{fontSize:9,padding:"1px 6px",background:C.purple,color:"#fff",borderRadius:50,fontWeight:700}}>{callbackTime}</span>}
          {lead.lead_temperature === "hot" && !isStalled && <span style={{fontSize:9,padding:"1px 6px",background:C.warm,color:"#fff",borderRadius:50,fontWeight:700}}>🔥 HOT</span>}
          {lead.cadence_day !== undefined && lead.cadence_day > 0 && <span style={{fontSize:9,padding:"1px 6px",background:C.light,color:C.muted,borderRadius:50,fontWeight:600}}>Day {lead.cadence_day}</span>}
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:2}}>
          {lead.interest && <span style={{marginRight:8}}>{lead.interest}</span>}
          <span style={{fontFamily:"monospace",color:C.text,fontWeight:600}}>{lead.phone || "no phone"}</span>
        </div>
        {lead.last_reply_text && (
          <div style={{fontSize:10,color:C.muted,fontStyle:"italic",marginTop:2}}>💬 "{lead.last_reply_text.substring(0,80)}{lead.last_reply_text.length > 80 ? "..." : ""}"</div>
        )}
      </div>

      {/* Actions */}
      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        <div style={{textAlign:"right",minWidth:60}}>
          {lead.estimated_value > 0 && <div style={{fontSize:13,fontWeight:700,color:C.green}}>${lead.estimated_value}</div>}
          <div style={{fontSize:10,color:C.dim}}>{ago} ago</div>
        </div>
        <button style={{padding:"8px 14px",background:isStalled?C.hot:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>
          {isStalled ? "🔥 Take it!" : "→ Open"}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// LEAD CALL PANEL - the full action panel
// ──────────────────────────────────────────────
function LeadCallPanel({ lead, onClose }) {
  const [fullLead, setFullLead] = useState(lead);
  const [messages, setMessages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsText, setSmsText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [showCallback, setShowCallback] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    load();
  }, [lead.id]);

  const load = async () => {
    const { data: l } = await supabase.from("leads").select("*").eq("id", lead.id).single();
    if (l) setFullLead(l);
    const { data: msgs } = await supabase.from("lead_messages").select("*").eq("lead_id", lead.id).order("created_at", { ascending: true }).limit(100);
    setMessages(msgs || []);
    const { data: acts } = await supabase.from("lead_activities").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false }).limit(50);
    setActivities(acts || []);
    const { data: tpls } = await supabase.from("lead_sms_quick_templates").select("*").eq("is_active", true).order("display_order");
    setSmsTemplates(tpls || []);
  };

  const copyPhone = () => {
    if (!fullLead.phone) return;
    navigator.clipboard.writeText(fullLead.phone);
    setPhoneCopied(true);
    setTimeout(() => setPhoneCopied(false), 2000);
  };

  const startCalling = async () => {
    setLoading(true);
    await supabase.from("leads").update({
      pipeline_status: "calling",
      bot_paused: true,
      call_attempts: (fullLead.call_attempts || 0) + 1,
      last_call_at: new Date().toISOString(),
    }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id,
      activity_type: "call_started",
      channel: "phone",
      direction: "outbound",
      content: "Started call - bot paused",
    });
    setLoading(false);
    await load();
  };

  const markBooked = async () => {
    await supabase.from("leads").update({
      pipeline_status: "booked", booked: true, status: "booked",
      cadence_paused: true, cadence_pause_reason: "booked",
    }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "booked",
      channel: "system", content: "🎉 Booked!",
    });
    await load();
    setTimeout(onClose, 800);
  };

  const markNoAnswer = async () => {
    const followUpAt = new Date(Date.now() + 2*60*60*1000);
    await supabase.from("leads").update({
      pipeline_status: "to_call",
      callback_scheduled_at: followUpAt.toISOString(),
      callback_note: "No answer - retry in 2h",
      bot_paused: false,  // re-enable bot to keep nurturing
    }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "no_answer",
      channel: "phone", direction: "outbound",
      content: `No answer - will retry at ${followUpAt.toLocaleTimeString()}`,
    });
    await load();
    setTimeout(onClose, 500);
  };

  const scheduleCallback = async (date, time, note) => {
    const dt = new Date(`${date}T${time}`);
    await supabase.from("leads").update({
      pipeline_status: "callback_scheduled",
      callback_scheduled_at: dt.toISOString(),
      callback_note: note,
      bot_paused: false,
    }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "callback_scheduled",
      channel: "phone", content: `Callback: ${dt.toLocaleString()} - ${note}`,
    });
    setShowCallback(false);
    await load();
    setTimeout(onClose, 500);
  };

  const markLost = async (reason) => {
    await supabase.from("leads").update({
      pipeline_status: "lost", lost_reason: reason,
      cadence_paused: true, cadence_pause_reason: reason,
    }).eq("id", lead.id);
    await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "lost",
      channel: "system", content: `Lost: ${reason}`,
    });
    setShowLost(false);
    await load();
    setTimeout(onClose, 500);
  };

  const applyTemplate = (t) => {
    const msg = t.content
      .replace(/\{\{first_name\}\}/g, fullLead.first_name || fullLead.full_name?.split(" ")[0] || "there")
      .replace(/\{\{book_link\}\}/g, "https://www.elyonhq.com/book")
      .replace(/\{\{callback_time\}\}/g, fullLead.callback_scheduled_at ? new Date(fullLead.callback_scheduled_at).toLocaleTimeString() : "soon");
    setSmsText(msg);
  };

  const sendSMS = async () => {
    if (!smsText.trim() || !fullLead.phone) return;
    setLoading(true);
    try {
      await supabase.functions.invoke("send-lead-sms", {
        body: { lead_id: lead.id, message: smsText },
      });
      await supabase.from("lead_activities").insert({
        lead_id: lead.id, activity_type: "sms_sent",
        channel: "sms", direction: "outbound", content: smsText,
      });
      await supabase.from("lead_messages").insert({
        lead_id: lead.id, direction: "outbound", channel: "sms",
        body: smsText, sent_by: "human",
      });
      setSmsText("");
      await load();
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "note_added",
      channel: "system", direction: "system", content: noteText,
    });
    setNoteText("");
    await load();
  };

  const isStalled = fullLead.bot_conversation_state === "stalled";

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:2000,display:"flex",alignItems:"stretch",justifyContent:"flex-end",backdropFilter:"blur(6px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",width:"100%",maxWidth:620,height:"100vh",overflow:"auto",boxShadow:"-8px 0 40px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column"}}>
        
        {/* Header */}
        <div style={{
          padding:"18px 22px",
          borderBottom:"1px solid "+C.border,
          background: isStalled ? "linear-gradient(135deg,rgba(220,38,38,0.12),rgba(220,38,38,0.04))" : "linear-gradient(135deg,"+C.goldBg+",rgba(180,140,60,0.04))",
          position:"sticky",top:0,zIndex:10,
        }}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                {isStalled && <span style={{fontSize:18}}>🔥</span>}
                <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>{fullLead.full_name || "Anonymous"}</div>
                {isStalled && <span style={{padding:"3px 10px",background:C.hot,color:"#fff",borderRadius:50,fontSize:10,fontWeight:700,letterSpacing:"0.05em"}}>STALLED</span>}
              </div>
              <div style={{fontSize:11,color:C.muted}}>From {fullLead.source} · {new Date(fullLead.created_at).toLocaleString()}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:24,padding:0,lineHeight:1}}>×</button>
          </div>

          {isStalled && (
            <div style={{padding:"8px 12px",background:C.hot,color:"#fff",borderRadius:8,fontSize:12,fontWeight:600,marginBottom:8}}>
              🔥 This customer was actively chatting with the bot and went silent. Call NOW for max conversion!
            </div>
          )}

          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <Pill color={fullLead.lead_temperature === "hot" ? C.hot : C.warm}>{(fullLead.lead_temperature || "warm").toUpperCase()}</Pill>
            <Pill color={C.gold}>Score {fullLead.lead_score || 50}</Pill>
            {fullLead.estimated_value > 0 && <Pill color={C.green}>${fullLead.estimated_value}</Pill>}
            {fullLead.call_attempts > 0 && <Pill color={C.blue}>📞 {fullLead.call_attempts} call{fullLead.call_attempts > 1 ? "s" : ""}</Pill>}
            {fullLead.cadence_day !== undefined && <Pill color={C.purple}>Day {fullLead.cadence_day}</Pill>}
          </div>
        </div>

        <div style={{padding:22,flex:1}}>
          {/* PHONE - HUGE */}
          {fullLead.phone && (
            <div style={{padding:"16px 18px",background:"linear-gradient(135deg,"+C.goldBg+","+C.borderGold+")",border:"2px solid "+C.gold,borderRadius:12,marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:120}}>
                  <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>📱 Call This Number</div>
                  <div style={{fontSize:24,fontWeight:800,color:C.text,fontFamily:"monospace",letterSpacing:"0.05em"}}>{fullLead.phone}</div>
                </div>
                <button onClick={copyPhone} style={{padding:"14px 22px",background:phoneCopied?C.green:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow:"0 2px 8px rgba(180,140,60,0.3)"}}>
                  {phoneCopied ? "✓ Copied!" : "📋 Copy Number"}
                </button>
              </div>
            </div>
          )}

          {/* INTEREST */}
          {(fullLead.interest || fullLead.notes) && (
            <Section title="💭 What they want">
              {fullLead.interest && <div style={{padding:"10px 14px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:8,marginBottom:6,fontSize:13,fontWeight:600,color:C.text}}>{fullLead.interest}</div>}
              {fullLead.notes && <div style={{padding:"10px 14px",background:C.light,borderRadius:8,fontSize:12,color:C.muted}}>{fullLead.notes}</div>}
            </Section>
          )}

          {/* CONVERSATION (SMS history) */}
          {messages.length > 0 && (
            <Section title="💬 Conversation">
              <div style={{maxHeight:300,overflow:"auto",padding:"4px",background:C.light,borderRadius:8}}>
                {messages.map(m => (
                  <div key={m.id} style={{
                    margin:"6px",
                    padding:"8px 12px",
                    background: m.direction === "inbound" ? "#fff" : (m.sent_by === "bot" ? "rgba(124,58,237,0.1)" : "rgba(180,140,60,0.15)"),
                    border:"1px solid " + (m.direction === "inbound" ? C.border : (m.sent_by === "bot" ? "#7c3aed40" : C.borderGold)),
                    borderRadius: 9,
                    maxWidth: "85%",
                    marginLeft: m.direction === "inbound" ? "0" : "auto",
                  }}>
                    <div style={{fontSize:9,color:C.muted,marginBottom:3,fontWeight:700,textTransform:"uppercase"}}>
                      {m.direction === "inbound" ? "💬 Them" : (m.sent_by === "bot" ? "🤖 Bot" : "👤 " + (m.user_name || "You"))} · {new Date(m.created_at).toLocaleTimeString()}
                    </div>
                    <div style={{fontSize:12,color:C.text,whiteSpace:"pre-wrap"}}>{m.body}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* QUICK ACTIONS */}
          <Section title="⚡ Action">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {fullLead.pipeline_status !== "calling" && (
                <ActionBtn color={C.warm} onClick={startCalling} disabled={loading}>☎️ Start Calling</ActionBtn>
              )}
              <ActionBtn color={C.green} onClick={markBooked} disabled={loading}>✅ Booked!</ActionBtn>
              {fullLead.pipeline_status === "calling" && (
                <>
                  <ActionBtn color={C.warm} onClick={markNoAnswer} disabled={loading}>📵 No Answer</ActionBtn>
                  <ActionBtn color={C.purple} onClick={()=>setShowCallback(true)} disabled={loading}>⏰ Schedule Callback</ActionBtn>
                </>
              )}
              {fullLead.pipeline_status !== "calling" && (
                <ActionBtn color={C.purple} onClick={()=>setShowCallback(true)} disabled={loading}>⏰ Schedule Callback</ActionBtn>
              )}
              <ActionBtn color={C.red} onClick={()=>setShowLost(true)} disabled={loading}>❌ Mark Lost</ActionBtn>
            </div>
          </Section>

          {/* SEND SMS */}
          <Section title="💬 Send SMS">
            <div style={{padding:8,background:C.light,borderRadius:8,marginBottom:8,maxHeight:140,overflow:"auto"}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,marginBottom:6,textTransform:"uppercase"}}>Quick templates:</div>
              {smsTemplates.map(t => (
                <button key={t.id} onClick={()=>applyTemplate(t)} style={{display:"block",width:"100%",padding:"6px 10px",marginBottom:3,background:"#fff",border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",textAlign:"left",fontFamily:F,fontSize:11}}>
                  <span style={{fontWeight:700,color:C.text}}>{t.name}</span>
                </button>
              ))}
            </div>
            <textarea value={smsText} onChange={e=>setSmsText(e.target.value)} rows={3} placeholder="Type or pick a template..." style={{...inp,resize:"vertical"}}/>
            <button onClick={sendSMS} disabled={loading || !smsText.trim() || !fullLead.phone} style={{marginTop:6,padding:"10px",background:loading||!smsText.trim()?C.light:"linear-gradient(135deg,"+C.blue+","+C.purple+")",color:loading||!smsText.trim()?C.muted:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:loading||!smsText.trim()?"not-allowed":"pointer",fontFamily:F,width:"100%"}}>
              {loading ? "Sending..." : `📤 Send SMS`}
            </button>
          </Section>

          {/* ADD NOTE */}
          <Section title="📝 Quick Note">
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} rows={2} placeholder="e.g. Said she'll call back tomorrow" style={{...inp,resize:"vertical"}}/>
            <button onClick={addNote} disabled={!noteText.trim()} style={{marginTop:6,padding:"8px",background:noteText.trim()?C.gold:C.light,color:noteText.trim()?"#fff":C.muted,border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:noteText.trim()?"pointer":"not-allowed",fontFamily:F,width:"100%"}}>Save Note</button>
          </Section>

          {/* TIMELINE */}
          <Section title="📜 Timeline">
            <div style={{maxHeight:240,overflow:"auto"}}>
              {activities.slice(0, 20).map(a => (
                <div key={a.id} style={{padding:"6px 10px",borderLeft:"2px solid "+C.borderGold,marginBottom:4,fontSize:11}}>
                  <div style={{color:C.text,fontWeight:600}}>{a.activity_type.replace(/_/g," ")}</div>
                  {a.content && <div style={{color:C.muted,marginTop:2}}>{a.content.substring(0,150)}</div>}
                  <div style={{fontSize:9,color:C.dim,marginTop:2}}>{new Date(a.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {showCallback && <ScheduleCallback onSchedule={scheduleCallback} onClose={()=>setShowCallback(false)}/>}
        {showLost && <LostReasonModal onSelect={markLost} onClose={()=>setShowLost(false)}/>}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
function ScheduleCallback({ onSchedule, onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("15:00");
  const [note, setNote] = useState("");

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:380,width:"100%",padding:20}}>
        <div style={{fontSize:16,fontWeight:800,color:C.purple,fontFamily:S,marginBottom:14}}>⏰ Schedule Callback</div>
        <Field><Label>Date</Label><input type="date" value={date} onChange={e=>setDate(e.target.value)} min={today} style={inp}/></Field>
        <Field><Label>Time</Label><input type="time" value={time} onChange={e=>setTime(e.target.value)} style={inp}/></Field>
        <Field><Label>Note (optional)</Label><input value={note} onChange={e=>setNote(e.target.value)} placeholder="What did they say?" style={inp}/></Field>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={onClose} style={{flex:1,padding:"9px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={()=>onSchedule(date, time, note)} style={{flex:2,padding:"9px",background:"linear-gradient(135deg,"+C.purple+",#a855f7)",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Schedule</button>
        </div>
      </div>
    </div>
  );
}

function LostReasonModal({ onSelect, onClose }) {
  const reasons = ["Not interested", "Too expensive", "Going elsewhere", "Wrong number", "No response after 5+ tries", "Other"];
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:380,width:"100%",padding:20}}>
        <div style={{fontSize:16,fontWeight:800,color:C.red,fontFamily:S,marginBottom:14}}>❌ Mark Lost - Why?</div>
        {reasons.map(r => (
          <button key={r} onClick={()=>onSelect(r)} style={{display:"block",width:"100%",padding:"10px 14px",marginBottom:5,background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:7,fontSize:13,cursor:"pointer",fontFamily:F,textAlign:"left"}}
            onMouseEnter={e=>e.currentTarget.style.background=C.redL}
            onMouseLeave={e=>e.currentTarget.style.background=C.light}>{r}</button>
        ))}
        <button onClick={onClose} style={{marginTop:6,width:"100%",padding:"8px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
      </div>
    </div>
  );
}

function BigStat({ label, value, color, highlight }) {
  return (
    <div style={{
      padding:14,
      background: highlight ? "linear-gradient(135deg,"+color+"15,"+color+"05)" : C.card,
      border: "1px solid " + (highlight ? color : C.border),
      borderLeft: "4px solid " + color,
      borderRadius:10,
      transition:"all 0.2s",
    }}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color:highlight?color:C.text,fontFamily:S}}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:18}}>
      <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{title}</div>
      {children}
    </div>
  );
}

function Pill({ color, children }) {
  return <span style={{padding:"3px 10px",background:color+"20",color:color,borderRadius:50,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{children}</span>;
}

function Field({ children }) {
  return <div style={{marginBottom:10}}>{children}</div>;
}

function Label({ children }) {
  return <label style={{display:"block",fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{children}</label>;
}

function ActionBtn({ color, onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:"11px 12px",
      background: disabled ? C.light : "linear-gradient(135deg,"+color+","+color+"dd)",
      color: disabled ? C.muted : "#fff",
      border: "none", borderRadius: 8,
      fontSize: 12, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: F, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}
